<template>
  <ion-page ref="pageElement" id="tracking-page">
    <ion-header>
      <MainToolbar :isSimple="simpleToolbar" />
    </ion-header>
    <ion-content>
      <ion-refresher slot="fixed" :disabled="!reportLoadingFinished" :class="(!reportLoadingFinished) ? 'loading' : 'loading-ready'" @ionRefresh="refreshReportIndex($event.target)">
        <ion-refresher-content></ion-refresher-content> <!-- TODO Make refresher spin until all the reports have been loaded. On first opening after login none have loaded yet! -->
      </ion-refresher>
      <div class="date-chip-container">
        <ion-chip color="primary" outline :class="['date-chip', 'sub-text', 'highlight', ((isSelectingDate) ? 'active' : undefined)]" @click="toggleSelectDate()">
          <ion-icon :icon="calendarNumber" class="chip-icon"></ion-icon>
          <ion-label v-if="selectedDay != null">{{ dayToLocalFormatShort(selectedDay) }}</ion-label>
          <ion-label v-else>{{ i18n.$t('tracking.no-day-selected-hint-short') }}</ion-label>
          <span class="chip-end-placeholder"></span>
        </ion-chip>
        <span class="sub-text">&mdash;</span>
        <ion-chip :color="(selectedTimespanDay != null) ? 'primary' : 'medium'" outline :class="['date-chip', 'sub-text', ((isSelectingTimespanDate) ? 'active' : undefined), ((selectedTimespanDay != null) ? 'highlight' : undefined)]" @click.stop="toggleSelectEndDate()">
          <ion-icon :icon="calendar" class="chip-icon"></ion-icon>
          <ion-label v-if="selectedTimespanDay != null">{{ dayToLocalFormatShort(selectedTimespanDay) }}</ion-label>
          <ion-label v-else><b>+</b> {{ i18n.$t('tracking.to-day-hint') }}</ion-label>
          <ion-icon v-if="selectedTimespanDay != null" :icon="closeCircle" @click.stop="selectedTimespanDay = null" class="chip-close"></ion-icon>
          <span v-else class="chip-end-placeholder"></span>
        </ion-chip>
      </div>

      <Calendar :class="(isSelectingDate || isSelectingTimespanDate) ? 'calendar' : 'calendar hidden'" v-model:selectedDay="currentlySelectingDay" v-model:selectedMonth="selectedMonth" :loadedDates="reportIndexForCurrentSelectedMonth" :minDate="isSelectingTimespanDate ? selectedDay : undefined" />

      <hr class="full-width">

      <div class="searchbar-filter-container">
        <ion-searchbar :disabled="isOffline" clearIcon="close-circle" :placeholder="i18n.$t(isOffline ? 'default_interaction.search_offline' : 'tracking.search')" @keyup="blurOnEnter" @ionBlur="activeSearch = $event.target.value" @ionClear="activeSearch = null"></ion-searchbar>
        <ion-button :disabled="!Object.keys(availableUnusedFilters).length" @click="editFilter($event)" shape="round" fill="clear" color="primary" class="filter-edit-button">
          <ion-icon slot="icon-only" :icon="filter"></ion-icon>
          <b>+</b>
        </ion-button>
      </div>
      <div class="filter-container" v-if="Object.keys(activeFilters).length > 0">
        <div class="chip-container">
            <ion-chip v-for="filterType in Object.keys(activeFilters).sort()" :key="filterType" color="tertiary" class="filter-chip" outline>
              <ion-icon :icon="getFilterIcon(filterType)" @click="editFilter($event, filterType)" class="chip-icon"></ion-icon>
              <ion-label @click="editFilter($event, filterType)">
                <span v-if="availableFilters[filterType] != null" class="chip-filter-name">{{availableFilters[filterType].localizedName }}: </span>
                <span v-if="activeFilters != null && Array.isArray(activeFilters[filterType]) && activeFilters[filterType].length > 1">{{ activeFilters[filterType].length }} {{ i18n.$t('tracking.selected') }}</span>
                <span v-else-if="activeFilters != null && Array.isArray(activeFilters[filterType]) && activeFilters[filterType].length == 1">{{ getLocalizedFilterName(filterType, activeFilters[filterType][0]) }}</span>
                <span v-else>{{ getLocalizedFilterName(filterType, activeFilters[filterType]) }}</span>
              </ion-label>
              <ion-icon :icon="closeCircle" @click="removeFilter(filterType)" class="chip-close"></ion-icon>
            </ion-chip>
        </div>
      </div>
      
      <ion-item v-if="selectedDay == null" lines="none" class="fl-s1" id="selected-date">
        <ion-label>{{ i18n.$t('tracking.no-day-selected-hint') }}</ion-label>
      </ion-item>
      
      <ion-item lines="none" :class="['fl-s1', 'wl-light', ((filteredReportCount > 0) ? 'has-entries' : undefined)]" id="entries-hint">
        <ion-label v-if="filteredReportCount <= 0"><span>{{ i18n.$t('tracking.no-entries-hint') }}</span></ion-label>
        <ion-label v-else :key="filteredReportCount"><span>{{ i18n.$t('tracking.found-entries') }}: {{ filteredReportCount }}<ion-button @click="sortDescending = (!sortDescending)" shape="round" fill="clear" color="primary" class="change-order-button">
              <font-awesome-icon :icon="(sortDescending) ? faArrowDownWideShort : faArrowUpShortWide" />
            </ion-button>
          </span>
        </ion-label>

        
      </ion-item>
      <hr v-if="filteredReportCount > 0">
      <LimitedItemList :items="reverseArrayCondition(Object.keys(filteredReportIndex).sort(sortDate), sortDescending)" useItemAsKey displayType="month" startLines="none" endLines="none" :maximumVisibleItems="-1" :minimumVisibleItems="3" :visibleItemIncrement="3">
        <template v-slot:default="{ item: currentMonth }">
          <ion-item lines="none" class="current-month fl-s1">
            <ion-label><span>{{ monthToLocalFormat(currentMonth) }}</span></ion-label>
          </ion-item>
          <div v-for="currentDay in reverseArrayCondition(Object.keys(filteredReportIndex[currentMonth]).sort(sortDate), sortDescending)" :key="currentDay">
            <ion-item lines="none" class="current-date fl-s1">
              <ion-label><span>{{ dayInMonthToLocalFormat(currentDay, currentMonth) }}</span></ion-label>
            </ion-item>
            <ion-card v-for="(horseEntries, horse) in filteredReportIndex[currentMonth][currentDay].horses" :key="horse">
              <ion-card-header class="horse-name-header">
                <ion-card-title class="horse-name-title fl-0 wl-semibold">{{ (horse != 'null') ? getHorseName(horse) : i18n.$t('tracking.horse_not_assigned') }}</ion-card-title> <!-- TODO Split horse IDs here, before fetching to catch multiple horses -->
              </ion-card-header>

              <ion-card-content>
                <ion-list>
                  <ReportTrackingItem v-for="entry in reverseArrayCondition(Object.values(horseEntries).sort(sortByTimestamp), sortDescending)" :key="`${entry.connection}${entry.id}`" :entry="entry"></ReportTrackingItem>
                </ion-list>
              </ion-card-content>
            </ion-card>
          </div>
        </template>
      </LimitedItemList>
      <div class="over-scroll"></div>
    </ion-content>
    <ion-tab-bar id="sub-tabs">
      <ion-tab-button class="tracking-tab custom-tab-selected" disabled>
        <AnimatedLogo stopPattern="small" class="logo"></AnimatedLogo>
        <ion-label>{{ i18n.$t('tracking.title') }}</ion-label>
      </ion-tab-button>
    </ion-tab-bar>
  </ion-page>
</template>

<script>
import { IonPage, IonHeader, IonContent, IonCard, IonCardHeader, IonCardTitle, IonCardContent, IonList, IonItem, IonLabel, IonRefresher, IonRefresherContent, IonIcon, IonButton, IonSearchbar, IonChip, IonTabBar, IonTabButton, onIonViewDidLeave } from '@ionic/vue';
import { computed, ref, watch } from 'vue';
import { useStore } from 'vuex';


import { calendarNumber, calendar, closeCircle, search, filter, location, list, bookmark, gitCommit } from 'ionicons/icons';

import horseIcon from '@/assets/icons/horse.svg';

import MainToolbar from '@/components/MainToolbar.vue';
import AnimatedLogo from '@/components/AnimatedLogo.vue';
import Calendar from '@/components/Calendar.vue';
import ReportTrackingItem from '@/components/ReportTrackingItem.vue';

import LimitedItemList from '@/components/LimitedItemList.vue';

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faArrowDownWideShort, faArrowUpShortWide } from '@fortawesome/free-solid-svg-icons';

import { useI18n } from "@/utils/i18n";

import { apiErrorToast, localErrorToast } from '@/utils/error';

import { sortDate, sortByTimestamp } from '@/utils/report';

import { useDayjs } from '@/utils/dayjs';

import { blurOnEnter, handlePageLeave } from '@/utils/interaction';

import { mapLocationsWithOrder, getLocation, getAdditionalText, getSearchMetadata } from '@/utils/animals';

import { removeNonAlphaNumCharactersNormalize } from '@/utils/algorithms';

import { openFilterPopup, default as filterPopupComponent } from '@/components/FilterPopup.vue';

import _ from 'lodash';

export default  {
  name: 'Tracking',
  components: { IonHeader, IonContent, IonPage, MainToolbar, Calendar, IonCard, IonCardHeader, IonCardTitle, IonCardContent, IonList, IonItem, IonLabel, IonRefresher, IonRefresherContent, IonIcon, IonButton, IonSearchbar, IonChip, IonTabBar, IonTabButton, AnimatedLogo, ReportTrackingItem, LimitedItemList, FontAwesomeIcon },
  props: {
    simpleToolbar: Boolean
  },
  setup() {
    const i18n = useI18n();

    const store = useStore();

    const pageElement = ref(null);

    const { dayjs, parseAndFormat } = useDayjs();

    const isSelectingDate = ref(false);

    const isSelectingTimespanDate = ref(false);

    const sortDescending = ref(true);

    const FALLBACK_AVATAR = horseIcon;
    const FALLBACK_AVATAR_IS_FONT_AWESOME = false;

    const isOffline = computed(() => {
      return !(store.getters.isOnline);
    });

    const setSelectedMonthFromDay = function(day){
      if (day != null) {
        let currentDay = dayjs.utc(day, 'DD.MM.YYYY');
        if (currentDay != null && currentDay.isValid()) {
          selectedMonth.value = currentDay.format('MM.YYYY');
          return true;
        }
      }
      return false;
    }

    const toggleSelectDate = function(){
      //If we make it visible, set current month to the month of the current day
      if (!isSelectingDate.value) setSelectedMonthFromDay(selectedDay.value);
      isSelectingDate.value = !isSelectingDate.value;
      isSelectingTimespanDate.value = false;
    }

    const toggleSelectEndDate = function(){
      //If we make it visible, set current month to the month of the current day
      if (!isSelectingTimespanDate.value) setSelectedMonthFromDay(selectedTimespanDay.value);
      isSelectingTimespanDate.value = !isSelectingTimespanDate.value;
      isSelectingDate.value = false;
    }

    const FILTER_ICONS = { //TODO Add icon for horse and control_examination
      stage: gitCommit,
      category: bookmark,
      type: list,
      location: location
    }

    const getFilterIcon = function(filterType) {
      if (filterType in FILTER_ICONS) {
        return FILTER_ICONS[filterType];
      }
      return filter;
    }

    const reportIndexForCurrentSelectedMonth = computed(() => store.getters['reports/getLocalizedReportCountsForCurrentSelectedMonth']);

    const activeSearch = ref(null);

    const searchedReports = ref(null);

    //Search is calculated immediately on load (empty) and when it or the index changes!
    watch([activeSearch, reportIndexForCurrentSelectedMonth], ([newSearch]) => {
      if (newSearch != null && newSearch.length) {
        //TODO Maybe also search reports in upload cache locally and add their tempId U+index. For now all of them are included in the applyFilter function, because there are not a lot.
        store.dispatch('reports/searchReports', newSearch)
        .then((searchedReportIndex) => {
          searchedReports.value = searchedReportIndex;
        })
        .catch((error) => {
          apiErrorToast(i18n, error);
          searchedReports.value = null;
        })
      } else { //No valid search term, reset filter
        searchedReports.value = null;
      }
    }, {deep: true, immediate: true});

    //TODO TEST: Filters should be reapplied automatically, when they change or the list of reports changes, becusee it will be a computed value

    const editFilter = function(event, filterType) {
      let value = null;
      if (activeFilters.value != null && activeFilters.value[filterType] != null) {
        value = activeFilters.value[filterType];
      }
      openFilterPopup(filterPopupComponent, event, _.assign({}, availableUnusedFilters.value, _.pick(availableFilters.value, filterType)), filterType, value, { 
          customNoEntriesHint: i18n.$t('filter.no_entries.filter_date_dependancy'),
          fallbackAvatarIcon: FALLBACK_AVATAR,
          fallbackAvatarIconIsFontAwesome: FALLBACK_AVATAR_IS_FONT_AWESOME,
        })
        .then((data) => {
          if (data != null && data.data != null && data.data['filter'] != null && data.data['value'] != null) {
            activeFilters.value[data.data['filter']] = data.data['value'];
          }
        })
    }

    const removeFilter = function(filterType) {
      if (filterType in activeFilters.value) 
        delete activeFilters.value[filterType];
    }

    const getLocalizedFilterName = computed(() => {
      return (filterType, filterValue) => {
        if (availableFilters.value != null && filterType in availableFilters.value && availableFilters.value[filterType].categories != null) {
          for (let category of Object.values(availableFilters.value[filterType].categories)) {
            for (let option of category.options) {
              if (_.isEqual(option.value, filterValue)) return option.localizedName;
            }
          }
        }
        else return '';
      }
    });

    //TODO Don't load all reports when date is undefined? Or load all? Check behaviuor when no date or date range is selected
    //TODO Maybe add filter later to include only reports with media (Needs search from API again!)

    const selectedDay = computed({
      get: () => store.getters['reports/getSelectedDay'],
      set: newValue => {
        store.dispatch('reports/updateSelectedDay', newValue);
      }
    });

    const selectedTimespanDay = computed({
      get: () => store.getters['reports/getSelectedTimespanDay'],
      set: newValue => {
        store.dispatch('reports/updateSelectedTimespanDay', newValue);
      }
    });

    const getLocalizedMainCategoryById = function(mainCategoryId){
      let categoryNames = store.getters['reports/getMainCategoryNamesById'](mainCategoryId);
      if (categoryNames) {
        return categoryNames[i18n.locale.value];
      } else {
        return null;
      }
    }

    const getHorseName = function(horseId){
      let horse = store.getters['horses/getPersonalInfoById'](horseId);
      if (horse) {
        return horse.name;
      } else {
        return null;
      }
    }

    const availableReportTypes = ref([]);

    const availableLocationsObject = computed(() => {
      return mapLocationsWithOrder(store.getters['customization/getLocations']);
    });

    const unassignedPlaceholder = computed(() => {
      return i18n.$t('animal_record.subject.unassigned');
    });

    const reportTypeFilters = computed(() => {
      if (availableReportTypes.value == null) return null;

      return _.mapValues(availableReportTypes.value['byHierarchy'], (examinationStage, examinationStageDescriptor) => {
        if (examinationStage == null && examinationStage.categories != null) return null;
        
        let options = [];

        _.forEach(examinationStage.categories, (category, categoryDescriptor) => {
          if (category.types != null) _.forEach(category.types, (type, typeDescriptor) => { //TODO How to treat spaces at the end or slightly different writing? Cleanup? Trim? So they don't appear twice!
            options.push({
              value: type.identifiers,
              localizedName: typeDescriptor, //TODO Translate descriptor. Get translated version in return from model?
              indicatorText: (categoryDescriptor != 'null') ? categoryDescriptor : undefined,
              order: parseInt(`${(category.order > 0) ? category.order : 0}${(type.order > 0) ? type.order : 0}`), //Take both order properties into account, category preceding type
              searchMetadata: [ //Has to be array of normalized strings
                removeNonAlphaNumCharactersNormalize(examinationStageDescriptor),
                removeNonAlphaNumCharactersNormalize(categoryDescriptor)
              ]
            });
          });
        })

        return {
          order: examinationStage.order,
          options
        }
      });
    });

    //Contains all available animals and an additional empty entry for an animal that is not contained in the available ones, but loaded
    const availableAnimalsByOrder = ref(null);

    //animalsWithStatusAsyncFunction updates, if the index of the statuses or animals update
    const animalsWithStatusPromise = computed(() => store.getters['horses/getHorsesByOrderAsync']);

    //Starts filtering when one of the dependencies changes and once on load
    watch(animalsWithStatusPromise, (newAnimalPromise) => {
      if (newAnimalPromise != null) { 
        newAnimalPromise
          .then((animalsByOrder) => {
            availableAnimalsByOrder.value = animalsByOrder.animals;
          })
          .catch(error => {
            console.error('Error retrieving animal data', error);
            localErrorToast(i18n, error);

            availableAnimalsByOrder.value = null;
          });
      } else {
        availableAnimalsByOrder.value = null;
      }
    }, {deep: true, immediate: true});

    const animalFilters = computed(() => {
      let animals = _.map(availableAnimalsByOrder.value, (animal, animalId) => {
        let location = getLocation(animal, unassignedPlaceholder.value, availableLocationsObject.value);
        return {
          value: animalId,
          localizedName: animal.name || i18n.$t('report.create.horse_no_name'),
          additionalText: getAdditionalText(animal),
          wrapAdditionalText: true,
          location: location,
          order: (location != null && location.spot != null) ? location.spot.order : undefined,
          indicatorText: (location != null && location.spot != null) ? location.spot.text : undefined,
          italic: animal.name == null,
          showAvatar: true,
          avatarSrc: (animal != null && animal.image != null) ? animal.image.blobURL : undefined,
          avatarColor: (animal != null) ? animal.color : undefined,
          searchMetadata: getSearchMetadata(animal, false)
        };
      });
      let animalsByLocation = _.reduce(animals, (result, animal) => {
        //Default unordered fallback
        let animalLocation = 'null';
        let animalLocationOrder = -2;
        if (animal != null && animal.location != null && animal.location.location != null) {
          if (animal.location.location.text != null) animalLocation = animal.location.location.text;
          if (animal.location.location.order != null) animalLocationOrder = animal.location.location.order;
        }

        //Either get existing object or create
        let locationObject = (result[animalLocation] || (result[animalLocation] = { order: animalLocationOrder, options: [] }));

        locationObject.options.push(animal);

        return result;
      }, {});
      
      let unassignedAnimalText = i18n.$t('tracking.filters.horse.not_assigned');

      //Add unassigned horses
      _.update(animalsByLocation, [unassignedAnimalText], (unassignedLocation) => {
        return {
          ...unassignedLocation,
          order: -3,
          options: _.concat((unassignedLocation != null && unassignedLocation.options != null) ? unassignedLocation.options : [], [{ value: 'null', localizedName: unassignedAnimalText, italic: true, order: -1 }])
        }
      });

      return animalsByLocation;
    });

    const mainCategoryFilters = computed(() => { //TODO If in the future as localized text, change from ID here
      if (availableReportTypes.value['mainCategories'] == null) return null;
      return { null: { options: _.map(availableReportTypes.value['mainCategories'], (category) => { return { value: category, localizedName: getLocalizedMainCategoryById(category) } }) } }; //TODO Order?
    });

    const stageFilters = computed(() => {
      let noStageText = i18n.$t('animal_record.protocols.no_examination_stage');

      return { null: { 
        options: _.compact(_.map(reportTypeFilters.value, (stage, stageDescriptor) => {
            if (stageDescriptor == 'null') return { value: 'null', localizedName: noStageText, italic: true, order: 0 }; //Always at the beginning, other options order is +1

            return { value: stageDescriptor, localizedName: stageDescriptor, order: (parseInt(stage.order) + 1) } //TODO store needs to give the localized versions too! And then translate above in the reportTypeFilers and here!
          }))
        }
      }
    });

    const availableLocationFilters = computed(() => {
      return { null: { options: _.map(store.getters['reports/getLocationValues'], (value) => { return { value, localizedName: i18n.$t(`report.location.${value}`) };}) } };
    });

    const availableFilters = computed(() => {
      return { //TODO Set order properties when they are ordered! Most of them have already.
        'stage': { order: 0, localizedName: i18n.$t('animal_record.protocols.examination_stage'), allowMultiple: true, categories: stageFilters.value },
        'category': { order: 1, localizedName: i18n.$t('tracking.filters.category'), allowMultiple: true, categories: mainCategoryFilters.value },
        'type': { order: 2, localizedName: i18n.$t('tracking.filters.type'), allowMultiple: true, categories: reportTypeFilters.value },
        'horse': { order: 3, localizedName: i18n.$t('tracking.filters.horse.name'), allowMultiple: true, categories: animalFilters.value },
        'location': { order: 4, localizedName: i18n.$t('tracking.filters.location'), allowMultiple: true, categories: availableLocationFilters.value },
        'control_examination': { order: 5, localizedName: i18n.$t('tracking.filters.control_examination.name'), categories: { 
          null: { options: [ {value: false, localizedName: i18n.$t('tracking.filters.control_examination.false')}, {value: true, localizedName: i18n.$t('tracking.filters.control_examination.true')} ] } }
        },
        'draft': { order: 6, localizedName: i18n.$t('tracking.filters.draft.name'), categories: { 
          null: { options: [ {value: false, localizedName: i18n.$t('tracking.filters.draft.false')}, {value: true, localizedName: i18n.$t('tracking.filters.draft.true')} ] } }
        }
      };
    });

    //Filters that change based on the currently visible report time range or other filters depending on each other - Depends on the list of animals
    const DYNAMIC_FILTERS = ['stage', 'category', 'type'];

    const activeFilters = computed({
      get: () => store.getters['reports/getFilters'],
      set: filters => {
        store.commit('reports/setFilters', filters); //TODO Might only work if it is re-set and not modified
      }
    });

    //Remove all the filters that are not available anymore, when they change, also done on start
    watch(availableFilters, (newFilters, oldFilters) => {
      //Only react when there is something to check and we have a change
      if (newFilters != null && activeFilters.value != null && !_.isEqual(newFilters, oldFilters)) {
        for (let currentFilter of DYNAMIC_FILTERS) {
          if (newFilters[currentFilter] != null && newFilters[currentFilter].categories != null && activeFilters.value[currentFilter] != null) {
            //Get only the filters that are in the active and in the available ones
            activeFilters.value[currentFilter] = _.intersectionWith(activeFilters.value[currentFilter],
                                                                    _.flatMap(newFilters[currentFilter].categories, (category) => category.options), //Take all the options without being sorted in categories
                                                                    (filter, availableFilterOption) => _.isEqual(filter, availableFilterOption.value)); //Compare the value of each option with the value of the active filter
            if (Array.isArray(activeFilters.value[currentFilter]) && activeFilters.value[currentFilter].length == 0) {
              delete activeFilters.value[currentFilter];
            }
          }
        }
      }
    }, {deep: true, immediate: true});

    const availableUnusedFilters = computed(() => {
      return _.omit(availableFilters.value, Object.keys(activeFilters.value));
    });
    
    //Filtered index is updated by watcher
    const filteredReportIndex = ref({}); //TODO Filtered index Only includes new ones not the old, updated ones, is that by design? Probably for the best. Keep in mind when searching in the API.

    //Sums up all the counts inside each day
    const filteredReportCount = computed(() => {
      if (filteredReportIndex.value != null) {
        let sum = _.sum(_.map(filteredReportIndex.value, (month) => {
          return _.sum(_.map(month, (day) => {
            return day.count;
          }));
        }));
        if (!isNaN(sum)) return sum;
      }
      return 0;
    });

    //asyncFilterFunction updates, if the index of the reports changes!
    const asyncFilterFunction = computed(() => store.getters['reports/getReportIndexFilterAsyncFunction']);

    //Get the prepared index of report IDs in the selected timespan using a getter
    const reportIdIndex = computed(() => store.getters['reports/getReportIDsForSelectedTimespan']);

    //Starts filtering when one of the dependencies changes and once on load
    watch([reportIdIndex, activeFilters, searchedReports, asyncFilterFunction], ([newReportIdIndex, newFilters, newSearchedReports, newAsyncFilterFunction]) => {
      if (newReportIdIndex != null && newAsyncFilterFunction != null) {
        newAsyncFilterFunction(newReportIdIndex, newFilters, newSearchedReports) //FIXME Connected reports show only one document, even though there are multiple ones. Rework search process completely!
          .then(({index, reportTypes}) => {
            filteredReportIndex.value = index;
            availableReportTypes.value = reportTypes;
          })
          .catch(error => {
            console.error('Error filtering reports', error);
            localErrorToast(i18n, error);

            filteredReportIndex.value = {};
            availableReportTypes.value = {};
          });
      } else {
        filteredReportIndex.value = {};
        availableReportTypes.value = {};
      }
    }, {deep: true, immediate: true});

    //Get and set the currently selected date that is edited, or none if nothing is selected
    const currentlySelectingDay = computed({
      get: () => {
        if (isSelectingDate.value) {
          return selectedDay.value;
        } else if (isSelectingTimespanDate.value) {
          return selectedTimespanDay.value;
        } else {
          return null
        }
      },
      set: newValue => {
        if (isSelectingDate.value) {
          selectedDay.value = newValue;
          isSelectingDate.value = false;
        } else if (isSelectingTimespanDate.value) {
          selectedTimespanDay.value = newValue;
          isSelectingTimespanDate.value = false;
        }
      }
    });

    const dayToLocalFormat = computed(() => {
      return (day) => {
        return parseAndFormat.value(day, 'LL', 'DD.MM.YYYY');
      }
    });

    const dayInMonthToLocalFormat = computed(() => {
      return (day, month) => {
        return dayToLocalFormat.value(`${day}.${month}`);
      }
    });

    const dayToLocalFormatShort = computed(() => {
      return (day) => {
        return parseAndFormat.value(day, 'L', 'DD.MM.YYYY');
      }
    });

    const monthToLocalFormat = computed(() => {
      return (month) => {
        return parseAndFormat.value(month, 'MMMM YYYY', 'MM.YYYY');
      }
    });

    const selectedMonth = computed({
      get: () => store.getters['reports/getSelectedMonth'],
      set: newValue => {
        store.dispatch('reports/updateSelectedMonth', newValue);
      }
    });

    const reportLoadingFinished = computed(() => store.getters['reports/allLoadsFinished']);

    const refreshReportIndex = function(refresher = null){
      store.dispatch('reports/fetchReportIndex')
        .then(() => {
          if (refresher != null){
            refresher.complete();
          }
        })
        .catch(error => {
          if (refresher != null){
            refresher.cancel();
          }
          apiErrorToast(i18n, error);
        });
    }

    const reverseArrayCondition = function(array, condition) {
      if (condition) {
        return array.reverse();
      }
      return array;
    }

    onIonViewDidLeave(() => handlePageLeave(pageElement.value));

    return {
      i18n,
      pageElement,
      selectedDay,
      selectedTimespanDay,
      sortDescending,
      currentlySelectingDay,
      dayInMonthToLocalFormat,
      dayToLocalFormatShort,
      monthToLocalFormat,
      sortDate,
      sortByTimestamp,
      selectedMonth,
      store,
      getHorseName,
      reportLoadingFinished,
      refreshReportIndex,
      reverseArrayCondition,
      isSelectingDate,
      isSelectingTimespanDate,
      isOffline,
      toggleSelectDate,
      toggleSelectEndDate,
      getFilterIcon,
      getLocalizedFilterName,
      reportIndexForCurrentSelectedMonth,
      filteredReportIndex,
      filteredReportCount,
      availableFilters,
      availableUnusedFilters,
      activeSearch,
      activeFilters,
      removeFilter,
      editFilter,
      blurOnEnter,
      calendarNumber,
      calendar,
      closeCircle,
      search,
      filter,
      faArrowDownWideShort,
      faArrowUpShortWide
    };
  }
}
</script>

<style>
.main-menu.menu-pane-visible ion-tab-bar {
  display: flex;
}
</style>

<style scoped>
/* While we are manually loading, show refresher and disable interaction with it */
ion-refresher.loading ion-refresher-content :deep(.refresher-refreshing) {
  display: flex;
}

ion-card {
  margin-top: 10px;
}

ion-searchbar {
  padding: 0px;
}

.current-date {
  position: -webkit-sticky; /* Safari */
  position: sticky;
  top: 0;
  z-index: 10;
}

.current-month {
  color: var(--ion-text-color);
}

#entries-hint {
  color: var(--ion-text-color);
  margin-top: 36px;
}

.current-date, .current-month, #entries-hint {
  --min-height: auto;
}

#entries-hint ion-label, .current-date ion-label, .current-month ion-label {
  margin-top: 5px;
  margin-bottom: 5px;
  display: flex;
  justify-content: center;
}

.current-month span {
  color: var(--ion-color-primary);
}

#entries-hint span, .current-date span {
  background: var(--ion-inner-background-shade);
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  border-radius: 20px;
}

#entries-hint.has-entries span {
  padding-block: 0px;
  padding-right: 0px;
}

#entries-hint ion-button {
  margin: 0px 0px 0px 4px;
  width: 2.5em;
  height: 2.25em;
  --padding-start: 0.1em;
  --padding-end: 0.1em;
  --ripple-color: transparent;
  --background: transparent;
  --background-focused: transparent;
  --background-activated: transparent;
  --background-hover: transparent;
}

#entries-hint ion-button::part(native)::after {
  display: none!important;
}

ion-card-content {
  padding-bottom: 0px;
}

.filter-container {
  padding-right: 10px;
  padding-left: 10px;
  margin-top: 0px;
  margin-bottom: 10px;
  display: flex;
  flex-direction: row;
  max-width: 100%;
  width: 100%;
}

.searchbar-filter-container {
  display: flex;
  padding-inline: 10px;
  column-gap: 10px;
  margin-bottom: 10px;
}

.chip-container {
  margin: 0px;
  padding: 0px;
  flex-grow: 1;
  flex-basis: 0px;
  flex-shrink: 1;
  display: flex;
  flex-flow: row wrap;
  width: 0%;
  column-gap: 10px;
  row-gap: 10px;
}

.date-chip-container {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  width: 100%;
  padding-inline: 10px;
  padding-top: 10px;
  column-gap: 10px;
}

.date-chip-container span {
  vertical-align: bottom;
  line-height: 38px;
}

.date-chip {
  margin: 0px;
  display: flex;
  justify-content: space-between;
  border-radius: var(--global-border-radius);
  border-color: var(--ion-border-color);
  background: transparent;
  height: 38px;
}

.date-chip.highlight .chip-icon {
  color: var(--ion-color-primary)!important;
}

.date-chip.active {
  color: var(--ion-text-color);
  background: var(--ion-inner-background-shade);
}

.filter-chip {
  margin: 0px;
  color: var(--ion-color-tertiary-text);
}

.calendar {
  transition: max-height 0.4s ease-in-out, box-shadow 0.4s ease-in-out, border-bottom-width 0.4s ease-in-out, border-top-width 0.4s ease-in-out, opacity 0.4s ease-in-out, margin-top 0.4s ease-in-out, margin-bottom 0.4s ease-in-out;
  max-height: 450px;
}

.calendar.hidden {
  box-shadow: 0px 0px 0px 0px rgba(0,0,0,0);
  border-bottom-width: 0px!important;
  border-top-width: 0px!important;
  opacity: 0;
  margin-top: 0px;
  margin-bottom: 0px;
  max-height: 0px;
}

ion-chip ion-label {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding-top: 10px;
  padding-bottom: 10px;
}

.chip-icon {
  padding: 5px 5px 5px 0px;
  margin-right: 0px;
}

.chip-close {
  color: var(--ion-color-medium);
  padding: 5px 0px 5px 5px;
  margin-left: 0px;
  height: 1em;
  width: 1em;
  min-width: 1em;
}

hr {
  background: var(--ion-border-color);
  height: 1px;
  border-radius: 2px;
  margin-left: auto;
  margin-right: auto;
  width: 50%;
}

hr.full-width {
  width: calc(100% - (2 * 12px));
  margin-block: 16px;
}

.filter-edit-button {
  --border-width: 1px;
  --border-color: transparent;
  height: 32px;
  width: 32px;
  --padding-start: 6px;
  --padding-end: 6px;
  --padding-top: 0px;
}

.filter-edit-button b {
  right: -30%;
  bottom: 15%;
  position: absolute;
  padding: 0px;
  margin: 0px;
  font-size: 1.2em;
}

.filter-edit-button ion-icon, .filter-edit-button b {
  pointer-events: none;
}

/* From Tab Bar */
ion-tab-bar {
  display: none;
}

.tracking-tab {
  opacity: 1.0;
  --color: var(--ion-color-quaternary);
  --color-selected: var(--ion-color-quaternary);
}

.tracking-tab ion-label {
  font-weight: var(--weight-level-semibold);
  font-size: 0.9em;
}

.logo {
  margin-top: 4px;
  font-size: 24px;
  margin-bottom: 5px;
}

</style>